import { useStore } from '@nanostores/react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { Button } from '~/components/ui/Button';
import { Dialog, DialogHeader } from '~/components/ui/Dialog';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { compatibleEnvStore } from '~/lib/stores/compatibility';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

export const CompatibilityDialog = () => {
  const shouldShow = useStore(compatibleEnvStore.shouldShow);
  const isChromium = useStore(compatibleEnvStore.isChromium);

  useEffect(() => {
    if (shouldShow) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.CompatibilityWarningDialogShown,
        },
      });
    }
  }, [shouldShow]);

  useEffect(() => {
    compatibleEnvStore.checkCompatability();
  }, []);

  if (!shouldShow) {
    return null;
  }

  if (!isChromium) {
    return <NonChromiumDialog />;
  }

  return null;
};

const NonChromiumDialog = () => {
  return (
    <Dialog
      open={true}
      width={576}
      height={576}
      showCloseButton={false}
      closeOnBackdropClick={false}
      modal
      contentClassName="flex flex-col p-10 justify-center text-center items-center gap-4"
    >
      <VisuallyHidden>
        <DialogHeader title="Incompatible browser" />
      </VisuallyHidden>
      <div className="flex gap-8 justify-center items-center mb-6">
        <div className="i-bolt:logos-chrome size-12" />
        <div className="i-bolt:logos-edge size-12" />
        <div className="i-bolt:logos-brave size-12" />
      </div>
      <h2 className="text-3xl font-semibold">Bolt works best in supported browsers</h2>
      <p className="text-bolt-ds-textSecondary text-base">
        Your current browser doesn’t support key technologies that Bolt relies on. For the best experience, consider
        using{' '}
        <a href="https://www.google.com/chrome/" target="_blank" className="text-bolt-ds-textPrimary underline">
          Chrome
        </a>
        ,{' '}
        <a
          href="https://www.microsoft.com/en-us/edge/download"
          target="_blank"
          className="text-bolt-ds-textPrimary underline"
        >
          Edge
        </a>
        , or another Chromium-based browser.
      </p>
      <div className="mt-4 flex gap-3 justify-center items-center">
        <Button
          size="md"
          variant="secondary"
          onClick={() => {
            compatibleEnvStore.dismiss();
          }}
        >
          Continue anyway (limited)
        </Button>
        <Button
          size="md"
          onClick={async () => {
            await navigator.clipboard.writeText(window.location.href);
            toast.success('The project link was copied');
          }}
        >
          Copy project link
        </Button>
      </div>
    </Dialog>
  );
};
